/**
 * Pricing groups (S3.1) — pure, zero I/O; the caller supplies dataset rows.
 *
 * Citations: DD!125 Pricing Group 定價群組 + Discribe-20 (chip 11):
 * - 商品端（一對一）: a product belongs to exactly ONE group (煞車片 A only
 *   Brakes 煞車組) — structural via the single scalar
 *   `ProductPricingFields.pricingGroupId`.
 * - 客戶端（一對多）: a customer holds one discount card per group — the
 *   大潤發 shape: Brakes 打 8 折 / Ignition 打 7 折 / Engine 打 9 折.
 *
 * Composition point (plan §S3.1): the group discount applies at the −Discount
 * stage AFTER determination, between the level discount and the quantity
 * discount (build-default order level → group → quantity; K133 = Q-L1).
 *
 * TODO(citation-needed): the `price_level` variant of `CustomerGroupDiscount`
 * ships as a row shape only (plan-mandated `{groupId → discountPct|levelId}`);
 * Discribe-20 draws no per-group LEVEL resolution semantics, so
 * `findCustomerGroupPctDiscount` ignores those rows (they resolve as
 * no-discount) until a board asset or xlsx DD row specifies the behavior.
 */

import type {
  CustomerGroupDiscount,
  CustomerGroupPctDiscount,
  PricingGroup,
  ProductPricingFields,
} from '@/types/api/pricing';
import { roundHalfUp2 } from './quantityPricing';

/** The product's ONE pricing group (Discribe-20 一對一); null when ungrouped. */
export function findProductPricingGroup(
  fields: ProductPricingFields | undefined,
  groups: PricingGroup[] | undefined
): PricingGroup | null {
  const groupId = fields?.pricingGroupId ?? null;
  if (groupId === null || groupId === '') return null;
  return groups?.find((group) => group.id === groupId) ?? null;
}

/**
 * The customer's discount card for a group (大潤發 shape). Returns the first
 * matching row of either kind; null when the customer holds no card for the
 * group.
 */
export function findCustomerGroupDiscount(
  rows: CustomerGroupDiscount[] | undefined,
  customerId: number,
  groupId: string
): CustomerGroupDiscount | null {
  return rows?.find((r) => r.customerId === customerId && r.groupId === groupId) ?? null;
}

/**
 * The customer's PERCENT discount card for a group — the only cited variant
 * (Discribe-20 折扣卡). `price_level` rows resolve null (typed TODO above).
 */
export function findCustomerGroupPctDiscount(
  rows: CustomerGroupDiscount[] | undefined,
  customerId: number,
  groupId: string
): CustomerGroupPctDiscount | null {
  const match = findCustomerGroupDiscount(rows, customerId, groupId);
  return match !== null && match.kind === 'discount_pct' ? match : null;
}

/**
 * Group discount: unitPrice × (1 − discountPct/100), ROUND_HALF_UP 2dp
 * (打8折 = discountPct 20 = ×0.80).
 */
export function applyGroupDiscount(unitPrice: number, discountPct: number): number {
  return roundHalfUp2(unitPrice * (1 - discountPct / 100));
}
